import type {
  CardCondition,
  CardFinish,
  CardGame,
  CardRarity,
} from "@/context/AppContext";
import { mapRarity } from "@/features/tcgApi";

export const CONDITION_MAP: Record<string, CardCondition> = {
  nm: "NM",
  lp: "LP",
  mp: "MP",
  hp: "HP",
  damaged: "Damaged",
  poor: "Damaged",
  "near mint": "NM",
  "lightly played": "LP",
  "moderately played": "MP",
  "heavily played": "HP",
};

// eBay File Exchange uses prose-style condition labels with trailing colons
// (e.g. "Near Mint or Better:" exactly as eBay's condition picker emits it).
// Lower-case the keys here and lower-case the lookup; the trailing colon is
// stripped before lookup so we don't have to enumerate both variants.
export const EBAY_CONDITION_MAP: Record<string, CardCondition> = {
  "near mint or better": "NM",
  "near mint": "NM",
  "lightly played": "LP",
  "lightly used": "LP",
  "moderately played": "MP",
  "moderately used": "MP",
  "heavily played": "HP",
  "heavily used": "HP",
  played: "MP",
  used: "LP",
  damaged: "Damaged",
  poor: "Damaged",
};

export const PC_CONDITION_MAP: Record<string, CardCondition> = {
  "brand new": "NM",
  "like new": "NM",
  "normal wear": "LP",
  "lightly played": "LP",
  good: "MP",
  "moderately played": "MP",
  acceptable: "HP",
  poor: "HP",
  "heavily played": "HP",
  damaged: "Damaged",
  "heavy wear": "Damaged",
};

export function parseCondition(raw: string): CardCondition {
  return CONDITION_MAP[raw.toLowerCase().trim()] ?? "NM";
}

export function parsePcCondition(raw: string): CardCondition {
  return PC_CONDITION_MAP[raw.toLowerCase().trim()] ?? "NM";
}

export function parseEbayCondition(raw: string): CardCondition {
  const cleaned = raw.toLowerCase().trim().replace(/:$/, "").trim();
  return EBAY_CONDITION_MAP[cleaned] ?? "NM";
}

export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

export interface ParsedCsvResult {
  rows: Record<string, string>[];
  isPriceCharting: boolean;
  isEbayFileExchange: boolean;
  isCardmarket: boolean;
}

export function parseCsv(csv: string): ParsedCsvResult {
  const lines = csv.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return {
      rows: [],
      isPriceCharting: false,
      isEbayFileExchange: false,
      isCardmarket: false,
    };
  }
  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
  const isPriceCharting =
    headers.includes("product-name") &&
    headers.includes("console-name") &&
    headers.includes("price-in-pennies");
  // eBay File Exchange template always carries the *Action column (eBay
  // appends parameters in parentheses like
  // "*Action(SiteID=US|Country=US|Currency=USD|Version=1193|CC=UTF-8)" so
  // we match by prefix, not equality) plus eBay's category-specific *C:*
  // attribute columns. Generic CSVs won't have CustomLabel + a *C:Card Name
  // column, so the combo is unmistakable.
  const isEbayFileExchange =
    headers.some((h) => h.startsWith("*action")) &&
    headers.includes("customlabel") &&
    headers.some((h) => h.startsWith("*c:card name"));
  // Cardmarket exports always carry the three id columns plus `cn` (the
  // collector number column). Generic CSVs won't have `cardmarketid` or
  // `tcgplayerid` together.
  const isCardmarket =
    headers.includes("cardmarketid") &&
    headers.includes("tcgplayerid") &&
    headers.includes("cn");
  const rows = lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = vals[i] ?? "";
    });
    return obj;
  });
  return { rows, isPriceCharting, isEbayFileExchange, isCardmarket };
}

export interface PcCard {
  name: string;
  variant: string;
  cardNumber: string;
  setName: string;
  marketPrice: number;
  costBasis: number;
  condition: CardCondition;
  quantity: number;
  notes: string;
  folder: string;
  /** Optional. Only the eBay File Exchange importer fills these; PriceCharting
   *  rows leave them undefined and fall back to defaults at execute time. */
  rarity?: CardRarity;
  /** Source game from the import. Defaults to `Pokemon` at execute time when
   *  absent so the older import paths keep working unchanged. eBay File
   *  Exchange rows fill this from `*C:Game` so a mixed-game CSV (TNK-128)
   *  routes each row to the right enrichment branch (pokemontcg.io vs
   *  Scryfall). */
  game?: CardGame;
  tcgArtist?: string;
  tcgTypes?: string[];
  tcgSupertype?: string;
  tcgSubtypes?: string[];
  /** MTG color identity in `W/U/B/R/G` codes (TNK-128). Filled from eBay's
   *  `*C:Attribute/MTG:Colour` when the row is a Magic card; Scryfall
   *  backfill (TNK-121) refines it post-import when the CSV column is
   *  empty (which it commonly is on Carduploader exports). */
  mtgColors?: string[];
  year?: string;
  finish?: CardFinish;
  language?: string;
  graded?: boolean;
  /** External URL of the card's front image, when the source CSV carries it.
   *  eBay File Exchange (`PicURL`'s first segment) populates this; other
   *  importers leave it undefined and the auto-image worker resolves via
   *  pokemontcg.io instead. See features/cardImageBackfill.ts. */
  imageFrontUrl?: string;
  /** Same as imageFrontUrl for the back. eBay `PicURL`'s second segment. */
  imageBackUrl?: string;
}

function parseFinish(raw: string): CardFinish | undefined {
  const r = raw.trim().toLowerCase();
  if (!r || r === "regular" || r === "normal" || r === "non-foil")
    return undefined;
  if (r === "holo" || r === "holofoil" || r === "foil") return "Holo";
  if (r === "reverse" || r === "reverse holo" || r === "reverse holofoil")
    return "Reverse Holo";
  if (r === "premium") return "Premium";
  return undefined;
}

const POKEMON_STAGE_VALUES = new Set([
  "basic",
  "stage 1",
  "stage 2",
  "vmax",
  "vstar",
  "v-union",
  "break",
  "mega",
  "level-up",
  "restored",
  "baby",
]);

const TRAINER_STAGE_VALUES = new Set([
  "supporter",
  "item",
  "stadium",
  "pokémon tool",
  "pokemon tool",
  "tool",
  "technical machine",
]);

function deriveSupertype(stageRaw: string): string | undefined {
  // eBay's *C:Stage field doubles as our supertype signal: Pokémon stages
  // (Basic, Stage 1, Stage 2, etc.) → "Pokémon"; Trainer subtypes
  // (Supporter, Item, Stadium, Tool) → "Trainer". Anything else (Energy,
  // unknown) leaves it undefined so the bundle engine falls back to its
  // existing heuristics.
  const first = stageRaw.split(",")[0]?.trim().toLowerCase() ?? "";
  if (!first) return undefined;
  if (POKEMON_STAGE_VALUES.has(first)) return "Pokémon";
  if (TRAINER_STAGE_VALUES.has(first)) return "Trainer";
  if (first === "energy") return "Energy";
  return undefined;
}

function parseStageSubtypes(stageRaw: string): string[] | undefined {
  // eBay sometimes emits compound values like "Basic, Ancient" or
  // "Basic, Future" — preserve both as subtypes so the bundle engine can use
  // either one. Trim + drop empties.
  const parts = stageRaw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : undefined;
}

function nameFromTitle(title: string): string {
  // Title format from CardUploader-style exports:
  //   "{Card Name} {Number} {Rarity} {Set...} Pokemon {Condition}"
  // Strip from the first number-like token onward — that token is the eBay
  // card number (e.g. "37/142" or "SWSH050"), and the name always sits to
  // its left. Falls back to the raw title if no number token is found.
  const stop = title.search(/\s+\S*\d/);
  if (stop <= 0) return title.trim();
  return title.slice(0, stop).trim();
}

const EBAY_RARITY_VALUES: CardRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Promo",
  "Trainer",
  "Energy",
  "Damaged",
  "Sealed",
];

function parseRarity(raw: string): CardRarity | undefined {
  const cleaned = raw.trim();
  if (!cleaned) return undefined;
  // Prefer an exact enum match (preserves intent on canonical values), then
  // fall back to the shared mapper so upstream variants like "Rare Holo",
  // "Double Rare", "Reverse Holo", "Trainer Gallery Rare Holo" land in the
  // right bucket instead of getting dropped to undefined.
  const exact = EBAY_RARITY_VALUES.find(
    (r) => r.toLowerCase() === cleaned.toLowerCase(),
  );
  if (exact) return exact;
  return mapRarity(cleaned);
}

export function parsePriceChartingRow(row: Record<string, string>): PcCard {
  const productName = row["product-name"] ?? "";
  const match = productName.match(/^(.+?)(?:\s+\[([^\]]+)\])?\s+#(\S+)$/);
  const name = match ? match[1].trim() : productName;
  const variant = match ? (match[2] ?? "") : "";
  const cardNumber = match ? match[3] : "";

  const setRaw = row["console-name"] ?? "";
  const setName = setRaw.replace(/^Pokemon\s+/i, "").trim() || setRaw;

  const marketPrice = (parseInt(row["price-in-pennies"] ?? "0") || 0) / 100;
  const costBasis = (parseInt(row["cost-basis-in-pennies"] ?? "0") || 0) / 100;
  const condition = parsePcCondition(row["condition-string"] ?? "");
  const quantity = parseInt(row["quantity"] ?? "1") || 1;
  const folder = row["folder"] ?? "";

  const noteParts: string[] = [];
  if (row["notes"]) noteParts.push(row["notes"]);
  if (row["grading-company"])
    noteParts.push(`Graded by ${row["grading-company"]}`);
  if (row["grading-cert-id"]) noteParts.push(`Cert #${row["grading-cert-id"]}`);
  if (row["id"]) noteParts.push(`PC ID: ${row["id"]}`);
  const notes = noteParts.join(" · ");

  return {
    name,
    variant,
    cardNumber,
    setName,
    marketPrice,
    costBasis,
    condition,
    quantity,
    notes,
    folder,
  };
}

/**
 * Substring → `CardGame` table for eBay's `*C:Game` attribute. Listed in
 * order of specificity — the first match wins. eBay's game category
 * names occasionally drift (with/without the trademark, with/without
 * "TCG"/"CCG"/"OCG"), so each entry uses a lowercase substring rather
 * than an exact-match for resilience.
 *
 * Extending this is the one-line cost when a new TCG is added to the
 * inventory: add a row, add a `CardGame` union member if needed, ship.
 * Every importer that funnels through `detectGameFromEbayRow` picks up
 * the new tag automatically.
 *
 * `null` for the value lets us register patterns that *should* be
 * recognized as "Other" — e.g. Disney Lorcana, Flesh and Blood, Dragon
 * Ball Super — without conflating them with the four named slots in
 * the `CardGame` union.
 */
const EBAY_GAME_MATCHERS: ReadonlyArray<{
  match: string;
  game: CardGame;
}> = [
  // Named slots — order matters: more specific strings first so that
  // "yu-gi-oh" doesn't get swallowed by anything broader.
  { match: "yu-gi-oh", game: "Yu-Gi-Oh" },
  { match: "yugioh", game: "Yu-Gi-Oh" },
  { match: "one piece", game: "One Piece" },
  { match: "magic: the gathering", game: "Magic" },
  { match: "magic the gathering", game: "Magic" },
  { match: "magic", game: "Magic" },
  { match: "pokémon", game: "Pokemon" },
  { match: "pokemon", game: "Pokemon" },
  // Recognized-but-not-individually-modeled TCGs land under "Other"
  // so they still get a real game tag instead of being mis-routed to
  // the legacy Pokemon fallback. Extend this list as new categories
  // appear in operator imports.
  { match: "lorcana", game: "Other" },
  { match: "flesh and blood", game: "Other" },
  { match: "digimon", game: "Other" },
  { match: "dragon ball", game: "Other" },
  { match: "final fantasy", game: "Other" },
  { match: "weiss schwarz", game: "Other" },
  { match: "cardfight", game: "Other" }, // Cardfight!! Vanguard
  { match: "metazoo", game: "Other" },
];

/**
 * Detect the source game from eBay's `*C:Game` attribute. Routes
 * recognized values through `EBAY_GAME_MATCHERS`; anything non-blank
 * but unrecognized lands as `"Other"` so the operator's category
 * choice survives the import even when we haven't seen the TCG before.
 * Blank `*C:Game` falls back to `"Pokemon"` for legacy CSVs that
 * predate the column being populated reliably.
 */
function detectGameFromEbayRow(row: Record<string, string>): CardGame {
  const raw = (row["*c:game"] || row["c:game"] || "").trim().toLowerCase();
  if (!raw) return "Pokemon";
  for (const { match, game } of EBAY_GAME_MATCHERS) {
    if (raw.includes(match)) return game;
  }
  return "Other";
}

/**
 * Map an MTG color name (or single letter) to the canonical Scryfall
 * single-letter code. eBay's `*C:Attribute/MTG:Colour` may emit prose
 * ("Black"), a single letter ("B"), or a comma-joined multi-color
 * string ("Black, Red"). Anything we can't recognize is dropped so we
 * never persist garbage tokens that would break the ManaCost component.
 */
function parseMtgColors(raw: string): string[] | undefined {
  const cleaned = raw.trim();
  if (!cleaned) return undefined;
  const out: string[] = [];
  for (const tok of cleaned.split(/[,;/|]/)) {
    const t = tok.trim().toLowerCase();
    if (!t) continue;
    if (t === "white" || t === "w") out.push("W");
    else if (t === "blue" || t === "u") out.push("U");
    else if (t === "black" || t === "b") out.push("B");
    else if (t === "red" || t === "r") out.push("R");
    else if (t === "green" || t === "g") out.push("G");
    // Colorless is the absence of any color identity, not a stored code.
  }
  return out.length > 0 ? out : undefined;
}

// Map a single eBay File Exchange row into the same PcCard shape the rest of
// the importer pipeline already consumes. Typed *C:* attribute columns are
// preferred over parsing the free-form *Title, since the title is just a
// concatenation of those fields anyway.
//
// TNK-128: branches on `*C:Game` so Magic rows don't pick up Pokémon-only
// interpretations (Attribute/MTG:Colour going into `tcgTypes` as if it
// were an energy type; Stage="Standard" being parsed as a Pokémon stage
// and yielding no supertype). The shared bits (name/set/price/condition/
// pic/artist/year/language/graded) come from a common extractor so the
// game-specific code stays small.
export function mapEbayFileExchangeRow(row: Record<string, string>): PcCard {
  const game = detectGameFromEbayRow(row);

  const typedName = (row["*c:card name"] || row["c:card name"] || "").trim();
  // Fall back to parsing *Title when the typed Card Name field is blank
  // (some promo/sealed exports skip it). Title format starts with the card
  // name before the card number token.
  const title = (row["*title"] || row["title"] || "").trim();
  const name = typedName || (title ? nameFromTitle(title) : "");

  const setName = (row["*c:set"] || row["c:set"] || "").trim();
  const cardNumber = (
    row["*c:card number"] ||
    row["c:card number"] ||
    ""
  ).trim();
  const variant = (row["c:variant"] || row["*c:variant"] || "").trim();
  const conditionRaw =
    row["c:card condition"] ||
    row["*c:card condition"] ||
    row["cd:card condition - (id: 40001)"] ||
    "";
  const condition = parseEbayCondition(conditionRaw);
  const startPrice =
    parseFloat(row["startprice"] ?? row["*startprice"] ?? "0") || 0;
  const quantity = parseInt(row["*quantity"] ?? row["quantity"] ?? "1") || 1;

  // SKU + eBay ItemID + Custom Label are the bits the seller will want to
  // match against later; bundle them into notes so they survive the import.
  const noteParts: string[] = [];
  const sku = (row["customlabel"] ?? "").trim();
  if (sku) noteParts.push(`SKU: ${sku}`);
  const itemId = (row["itemid"] ?? "").trim();
  if (itemId) noteParts.push(`eBay ItemID: ${itemId}`);
  const notes = noteParts.join(" · ");

  const artist = (row["*c:illustrator"] || row["c:illustrator"] || "").trim();
  const year = (
    row["*c:year manufactured"] ||
    row["c:year manufactured"] ||
    ""
  ).trim();
  const finish = parseFinish(row["*c:finish"] || row["c:finish"] || "");
  const languageRaw = (row["*c:language"] || row["c:language"] || "").trim();
  // Skip the default — only persist the field when it's something the seller
  // would actually want flagged on the card.
  const language =
    languageRaw && languageRaw.toLowerCase() !== "english"
      ? languageRaw
      : undefined;
  const gradedRaw = (row["*c:graded"] || row["c:graded"] || "")
    .trim()
    .toLowerCase();
  const graded =
    gradedRaw === "yes" ? true : gradedRaw === "no" ? false : undefined;

  // eBay supports up to 12 picture URLs as a pipe-delimited PicURL string.
  // Convention in the user's exports is front then back; everything beyond
  // is ignored at import time (the auto-image worker can only attach two).
  const picParts = (row["picurl"] || "")
    .split("|")
    .map((u) => u.trim())
    .filter((u) => u.length > 0);

  // Game-specific enrichment from eBay's *C:* attribute columns. Pokémon
  // and Magic share the column NAMES but the semantics diverge: stage
  // means card stage on Pokémon and set legality on Magic; Attribute/
  // MTG:Colour means energy type on Pokémon and color identity on Magic.
  const colourRaw = (
    row["*c:attribute/mtg:colour"] ||
    row["c:attribute/mtg:colour"] ||
    ""
  ).trim();
  const stageRaw = (row["*c:stage"] || row["c:stage"] || "").trim();
  const cardTypeRaw = (row["*c:card type"] || row["c:card type"] || "").trim();

  let tcgTypes: string[] | undefined;
  let tcgSupertype: string | undefined;
  let tcgSubtypes: string[] | undefined;
  let mtgColors: string[] | undefined;

  // Game-specific column routing. Three-way branch so future TCGs
  // (Yu-Gi-Oh, One Piece, Other) don't inherit Pokémon's stage parser
  // and silently mangle their fields. Adding a new branch is cheap:
  // identify which eBay column carries the game's "primary type"
  // signal, then route here.
  if (game === "Magic") {
    // Stage carries set legality ("Standard"), not a stage — ignore.
    // Card Type carries the primary type ("Enchantment", "Creature",
    // "Sorcery", "Instant", "Artifact", "Planeswalker", "Land",
    // "Tribal") which lands on shared tcgTypes so the filter UI
    // picks it up. Scryfall backfill (TNK-121) refines tcgSupertype /
    // tcgSubtypes via splitMtgTypeLine post-import.
    if (cardTypeRaw) tcgTypes = [cardTypeRaw];
    mtgColors = parseMtgColors(colourRaw);
  } else if (game === "Pokemon") {
    // Pokémon: Stage maps to supertype + subtypes; the
    // Attribute/MTG:Colour column on a Pokémon row carries the energy
    // type ("Fire"/"Water"/etc) which goes on tcgTypes.
    tcgTypes = colourRaw ? [colourRaw] : undefined;
    tcgSupertype = deriveSupertype(stageRaw);
    tcgSubtypes = parseStageSubtypes(stageRaw);
  } else {
    // Yu-Gi-Oh / One Piece / Other — capture Card Type into tcgTypes
    // when present (most TCG eBay categories populate it with the
    // game's primary type signal: Monster/Spell/Trap for YGO,
    // Character/Event/Stage for One Piece). Stage and Attribute
    // columns are deliberately skipped — each game's semantics
    // there are different enough that mis-parsing would mangle the
    // data. When per-game enrichment paths land, add a branch here.
    if (cardTypeRaw) tcgTypes = [cardTypeRaw];
  }

  return {
    name,
    variant,
    cardNumber,
    setName,
    marketPrice: startPrice,
    costBasis: 0,
    condition,
    quantity,
    notes,
    folder: "",
    game,
    rarity: parseRarity(row["*c:rarity"] ?? row["c:rarity"] ?? ""),
    tcgArtist: artist || undefined,
    tcgTypes,
    tcgSupertype,
    tcgSubtypes,
    mtgColors,
    year: year || undefined,
    finish,
    language,
    graded,
    imageFrontUrl: picParts[0],
    imageBackUrl: picParts[1],
  };
}

/**
 * Cardmarket CSV export — sample header:
 *   "quantity","name","set","rarity","cn","condition","language","isFirstEd",
 *   "isReverseHolo","isSigned","price","comment","cardmarketId","tcgplayerId"
 *
 * `set` is a friendly name ("Astral Radiance"); resolveSetCodeByName at
 * import time hydrates the `setCode` from pokemontcg.io. The boolean variant
 * flags fold into our existing `finish` (Reverse Holo) and the free-form
 * `variant` field ("1st Edition", "Signed") per TCG vernacular. `tcgplayerId`
 * and `cardmarketId` aren't stored — the Card schema doesn't have homes for
 * them.
 */
export function parseCardmarketRow(row: Record<string, string>): PcCard {
  const name = (row["name"] || "").trim();
  const setName = (row["set"] || "").trim();
  const cardNumber = (row["cn"] || "").trim();
  const rarity = parseRarity(row["rarity"] ?? "");
  const condition = parseCondition(row["condition"] ?? "NM");
  const quantity = parseInt(row["quantity"] ?? "1") || 1;
  const marketPrice = parseFloat(row["price"] ?? "0") || 0;

  const languageRaw = (row["language"] || "").trim();
  const language =
    languageRaw && languageRaw.toLowerCase() !== "english"
      ? languageRaw
      : undefined;

  const truthy = (s: string): boolean => {
    const v = s.trim().toLowerCase();
    return v === "x" || v === "true" || v === "yes" || v === "1";
  };
  const isReverseHolo = truthy(row["isreverseholo"] || "");
  const isFirstEd = truthy(row["isfirsted"] || "");
  const isSigned = truthy(row["issigned"] || "");

  // `finish` is the strict treatment axis (Holo / Reverse Holo / Premium).
  // `variant` stays free-form for provenance tags like "1st Edition" / "Signed".
  const finish: CardFinish | undefined = isReverseHolo
    ? "Reverse Holo"
    : undefined;
  const variantTags: string[] = [];
  if (isFirstEd) variantTags.push("1st Edition");
  if (isSigned) variantTags.push("Signed");
  const variant = variantTags.join(" ");

  const noteParts: string[] = [];
  const comment = (row["comment"] || "").trim();
  if (comment) noteParts.push(comment);
  const tcgplayerId = (row["tcgplayerid"] || "").trim();
  if (tcgplayerId) noteParts.push(`TCGplayer ID: ${tcgplayerId}`);
  const notes = noteParts.join(" · ");

  return {
    name,
    variant,
    cardNumber,
    setName,
    marketPrice,
    costBasis: 0,
    condition,
    quantity,
    notes,
    folder: "",
    rarity,
    finish,
    language,
  };
}

export function mapGenericRow(row: Record<string, string>): PcCard {
  return {
    name: row.name ?? "",
    variant: row.variant ?? "",
    cardNumber: row.cardnumber ?? row.number ?? "",
    setName: row.setname ?? row["set name"] ?? "",
    marketPrice: parseFloat(row.marketprice ?? row["market price"] ?? "0") || 0,
    costBasis: 0.06,
    condition: parseCondition(row.condition ?? "NM"),
    quantity: parseInt(row.quantity ?? "1") || 1,
    notes: row.notes ?? "",
    folder: row.storagelocation ?? row.location ?? "",
  };
}
